import { PermIdentity } from "@material-ui/icons";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Grid,
  TextField,
} from "@mui/material";
import Card from "components/UI/Card/Card";
import CardHeader from "components/UI/Card/CardHeader";
import CardIcon from "components/UI/Card/CardIcon";
import GridContainer from "components/UI/Grid/GridContainer";
import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { useInviteUsers } from "services/input-core/users/invite-users";

const InviteUser = () => {
  const { t } = useTranslation("user-list");
  const inputRef = useRef<HTMLInputElement | null>(null);

  const [emails, setEmails] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const { mutate: inviteUsers } = useInviteUsers();

  const transformEmails = (emails: string[]) => {
    return emails.map((email: string) => ({
      email,
      username: email,
    }));
  };

  const onSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setIsLoading(true);

    try {
      const transformedData = transformEmails(emails);
      await inviteUsers(transformedData, {
        onSuccess: () => {
          toast.success(t("Invitation sent successfully"));
          setIsLoading(false);
          setEmails([]);
        },
        onError: (error) => {
          console.error("Error inviting users:", error);
          toast.error(t("Error sending invitation"));
          setIsLoading(false);
        },
      });
    } catch (error) {
      toast.error(t("Error sending invitation"));
      setIsLoading(false);
    } finally {
      setIsLoading(false);
    }
  };

  const handleBlur = () => {
    const emailInput = inputRef.current;
    if (emailInput && emailInput.value.trim() !== "") {
      const event = new KeyboardEvent("keydown", {
        key: "Enter",
        code: "Enter",
        keyCode: 13,
        bubbles: true,
        cancelable: true,
      });
      emailInput.dispatchEvent(event);
    }
  };

  return (
    <GridContainer>
      <Card>
        <CardHeader color="rose" icon>
          <CardIcon color="rose">
            <PermIdentity />
          </CardIcon>
        </CardHeader>
        <Box p={2}>
          <Box style={{ margin: "20px 0" }}>
            <Alert severity="info">
              {t(
                "Use our email invite page to easily invite users to join Uverify.",
              )}
            </Alert>
          </Box>
          <form onSubmit={onSubmit}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={8} md={8}>
                <Autocomplete
                  multiple
                  disabled={isLoading}
                  options={[]}
                  freeSolo
                  value={emails}
                  onChange={(event, values, reason) => {
                    if (
                      reason === "createOption" ||
                      reason === "selectOption"
                    ) {
                      const lastValue = values[values.length - 1];
                      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                      if (lastValue && !emailRegex.test(lastValue.trim())) {
                        toast.error(t("Please enter a valid email address"));
                        return;
                      }
                    }
                    setEmails(values.map((value: string) => value.trim()));
                  }}
                  onBlur={handleBlur}
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => (
                      <Chip
                        variant="outlined"
                        label={option}
                        {...getTagProps({ index })}
                      />
                    ))
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      disabled={isLoading}
                      inputRef={inputRef}
                      label="E-mail(s)"
                      placeholder={t("type-an-email-and-press-enter")}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={4}>
                <Box width={160}>
                  <Button
                    style={{ height: "56px" }}
                    type="submit"
                    fullWidth
                    variant="contained"
                    color="primary"
                    disabled={isLoading}
                  >
                    {t("send-invite")}
                    {isLoading && (
                      <CircularProgress
                        style={{ color: "white", marginLeft: "10px" }}
                        size={20}
                      />
                    )}
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </form>
        </Box>
      </Card>
    </GridContainer>
  );
};

export default InviteUser;
